/**
 * Блок «Просрочка и риск» вкладки «Сроки годности»: фильтры, сортировка и
 * подписи списка. Чистые функции — их проверяет
 * `lib/__tests__/warehouse-expiry-risk.test.ts` без рендера. Полосы, проценты
 * и дни — из ответа бэкенда, здесь ничего из дат не выводится.
 */
import type {
  ExpiryBand,
  ExpiryHidden,
  ExpiryRiskRow,
  ExpiryWarehouse,
} from "@/api/warehouse_dashboard";
import { ruNum } from "@/lib/format";
import { fmtRubExact } from "@/lib/warehouse-overview";

export const ALL = "all";

export interface RiskFilter {
  band: ExpiryBand | typeof ALL;
  /** `riskWarehouseKey` или `ALL`. */
  warehouse: string;
  search: string;
}

export const EMPTY_RISK_FILTER: RiskFilter = { band: ALL, warehouse: ALL, search: "" };

export type RiskSortKey = "expires" | "cost" | "qty";
export type SortDir = "asc" | "desc";

export interface RiskSort {
  key: RiskSortKey;
  dir: SortDir;
}

/** Порядок ответа — «годен до» по возрастанию. */
export const DEFAULT_RISK_SORT: RiskSort = { key: "expires", dir: "asc" };

/** Ключ склада для фильтра: у разных юрлиц может быть один и тот же склад. */
export function riskWarehouseKey(organizationRef: string, warehouseRef: string): string {
  return `${organizationRef}|${warehouseRef}`;
}

export function filterRiskRows(rows: ExpiryRiskRow[], f: RiskFilter): ExpiryRiskRow[] {
  const needle = f.search.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (f.band === ALL || r.band === f.band) &&
      (f.warehouse === ALL ||
        riskWarehouseKey(r.organization_ref, r.warehouse_ref) === f.warehouse) &&
      (!needle ||
        r.article.toLowerCase().includes(needle) ||
        r.name.toLowerCase().includes(needle)),
  );
}

/**
 * Сортировка по клику на заголовок. Стабильная: при равных значениях остаётся
 * порядок ответа. ₽ `null` (нет строки в «Запасы») — ВСЕГДА в конце, в обе
 * стороны: это не ноль, и при «дороже сверху» он не должен выглядеть самым
 * дешёвым, а при «дешевле сверху» — самым первым.
 */
export function sortRiskRows(rows: ExpiryRiskRow[], sort: RiskSort): ExpiryRiskRow[] {
  const sign = sort.dir === "asc" ? 1 : -1;
  const value = (r: ExpiryRiskRow): number | string | null =>
    sort.key === "expires" ? r.expires : sort.key === "qty" ? r.qty : r.cost_rub;
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const va = value(a.r);
      const vb = value(b.r);
      if (va === null || vb === null) {
        if (va === vb) return a.i - b.i;
        return va === null ? 1 : -1;
      }
      if (va < vb) return -sign;
      if (va > vb) return sign;
      return a.i - b.i;
    })
    .map(({ r }) => r);
}

/** Клик по заголовку: тот же столбец — сменить направление; другой — «годен
 *  до» по возрастанию (срочное сверху), ₽ и кол-во — по убыванию (крупное). */
export function nextRiskSort(current: RiskSort, key: RiskSortKey): RiskSort {
  if (current.key === key) return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  return { key, dir: key === "expires" ? "asc" : "desc" };
}

export interface WarehouseOption {
  key: string;
  label: string;
}

/** Склады для фильтра — из сводки ответа, по алфавиту «юрлицо · склад». */
export function riskWarehouseOptions(byWarehouse: ExpiryWarehouse[]): WarehouseOption[] {
  return byWarehouse
    .map((w) => ({
      key: riskWarehouseKey(w.organization_ref, w.warehouse_ref),
      label: `${w.organization_name} · ${w.warehouse_name}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "ru"));
}

/** «просрочено 5 дн» / «последний день» / «осталось 30 дн» — по `days_left`
 *  ответа (`expires − today` 1С), не по часам браузера. */
export function daysLeftText(daysLeft: number): string {
  if (daysLeft < 0) return `просрочено ${ruNum.format(-daysLeft)} дн`;
  if (daysLeft === 0) return "последний день";
  return `осталось ${ruNum.format(daysLeft)} дн`;
}

/** «Скрыто настройкой: ЕГРАШКИН (6 шт, 60 ₽)» или `null`. */
export function hiddenRiskLine(hidden: ExpiryHidden[]): string | null {
  if (hidden.length === 0) return null;
  return `Скрыто настройкой: ${hidden
    .map((h) => `${h.name} (${ruNum.format(h.qty)} шт, ${fmtRubExact(h.cost_rub)})`)
    .join("; ")}`;
}
